import React, { FC, memo, useContext, useMemo } from "react";
import { replaceAllOccurence, useEffectOnce } from "@swiggy-private/core-ui";
// types
import { HalfcardVariants, PreStateData, States } from "@super-ui/components/m2c-swiggy-one-halfcard/types";
// components
import PreStateCtas from "./Components/PreStateCtas";
import VariantXpFirst from "./Components/VariantXpFirst";
import VariantXpSecond from "./Components/VariantXpSecond";
// styles
import { BullsEyeHalfCard } from "./styles";
// contexts
import { M2CHalfcardContext } from "@super-ui/contexts";
// constants
import { M2C_HALFCARD_FREQUENCY_CAPPING_KEY } from "@super-ui/components/m2c-halfcard/constants";
import { replaceAttributes } from "@super-ui/components/m2c-swiggy-one-halfcard/helpers";
import { COLOR_TAGS, STRIKED_TAGS } from "@super-ui/components/m2c-swiggy-one-halfcard/constants";

interface PreStateLayoutProps extends PreStateData {
    addingSuperToCart: boolean;
    setAddingSuperToCart: (value: boolean) => void;
    closeBottomSheet: (ctaText: string) => void;
    setStateToRender: (state: States) => void;
    preStateHeightRef: React.RefObject<HTMLDivElement>;
    isOneLite?: boolean;
}

const PreStateLayout: FC<PreStateLayoutProps> = props => {
    const { bottomSheetData, addingSuperToCart, setAddingSuperToCart, preStateHeightRef, isOneLite } = props;
    const { ctas, designVariantId = HalfcardVariants.DEFAULT, title, subtitles, attributeValues } = bottomSheetData;
    const {
        analyticsEvents: { onImpressionM2CHalfcard },
        frequencyCappingKey = M2C_HALFCARD_FREQUENCY_CAPPING_KEY,
    } = useContext(M2CHalfcardContext);

    const formattedTitle = useMemo(() => {
        const updatedTitle = replaceAttributes(title, attributeValues, COLOR_TAGS);
        return replaceAllOccurence(updatedTitle, STRIKED_TAGS.TO_FIND, STRIKED_TAGS.TO_BE_REPLACED);
    }, [attributeValues, title]);

    const formattedSubtitles = useMemo(() => {
        return subtitles?.map(subtitle => replaceAttributes(subtitle, attributeValues));
    }, [attributeValues, subtitles]);

    useEffectOnce(() => onImpressionM2CHalfcard(formattedTitle, frequencyCappingKey));

    const isVariantV1 = designVariantId?.toUpperCase() === HalfcardVariants.DEFAULT;

    const updatedBottomSheetData = useMemo(
        () => ({ ...bottomSheetData, title: formattedTitle, subtitles: formattedSubtitles }),
        [bottomSheetData, formattedTitle, formattedSubtitles],
    );

    // Define variants based on designVariantId VARIANT_V1: DEFAULT or VARIANT_V2: NEW
    const variants: { [key: string]: JSX.Element } = useMemo(
        () => ({
            [HalfcardVariants.DEFAULT]: <VariantXpFirst {...updatedBottomSheetData} isOneLite={isOneLite} />,
            [HalfcardVariants.NEW]: <VariantXpSecond {...updatedBottomSheetData} isOneLite={isOneLite} />,
        }),
        [updatedBottomSheetData, isOneLite],
    );

    const variantToMount = useMemo(() => variants[designVariantId.toUpperCase()], [designVariantId, variants]);

    return (
        <BullsEyeHalfCard ref={preStateHeightRef} isOneLite={isOneLite} isVariantV1={isVariantV1}>
            {variantToMount}
            <PreStateCtas
                {...props}
                ctas={ctas}
                addingSuperToCart={addingSuperToCart}
                setAddingSuperToCart={setAddingSuperToCart}
            />
        </BullsEyeHalfCard>
    );
};

export default memo(PreStateLayout);
